
import { Union, Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { union_type, list_type, class_type, option_type, bigint_type, bool_type, record_type, int32_type, string_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { exprContainsParams, ParameterDefs, ParamDefinition, CompSlotName, tryIntOfParamInt, ParamSlot, sameSlot, ParamName, evaluateParamExpression, ParamExpression, bindingsOf, ParamName_$reflection } from "./ParameterTypes.js";
import { toArray, flatten, defaultArg } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { forAll as forAll_1, filter as filter_1, isEmpty as isEmpty_1, tryPick, keys, containsKey, toList, ofList as ofList_1, tryFind as tryFind_1, map as map_1, add, fold, empty } from "../fable_modules/fable-library-js.5.13.0/Map.js";
import { stringHash, arrayHash, equalArrays, safeHash, bigintHash, compareArrays, equals, comparePrimitives, compare } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { ofArray, append, contains as contains_1, length, forAll, exists, singleton as singleton_1, tail, isEmpty, sortDescending, head, reverse, fold as fold_1, cons, empty as empty_1, tryFind, filter, map, collect, choose } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { minElement, unionMany, intersect, filter as filter_2, union, isEmpty as isEmpty_2, toList as toList_1, ofSeq, singleton, empty as empty_2, add as add_1, contains, ofList } from "../fable_modules/fable-library-js.5.13.0/Set.js";
import { compare as compare_1, equals as equals_1, fromOne } from "../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { List_distinct } from "../fable_modules/fable-library-js.5.13.0/Seq2.js";
import { resolveCanvasAtBindings } from "./ComponentSlots.js";
import { Component, ComponentType, CustomComponentType, LoadedComponent } from "./CommonTypes.js";
import { forAll as forAll_2 } from "../fable_modules/fable-library-js.5.13.0/Seq.js";

/**
 * One link in an instance path: a custom component instance of ChildSheet placed on ParentSheet.
 */
export class InstancePathLink extends Record {
    constructor(ParentSheet, InstanceId, InstanceLabel, ChildSheet) {
        super();
        this.ParentSheet = ParentSheet;
        this.InstanceId = InstanceId;
        this.InstanceLabel = InstanceLabel;
        this.ChildSheet = ChildSheet;
    }
}

export function InstancePathLink_$reflection() {
    return record_type("ParameterAnalysis.InstancePathLink", [], InstancePathLink, () => [["ParentSheet", string_type], ["InstanceId", int32_type], ["InstanceLabel", string_type], ["ChildSheet", string_type]]);
}

/**
 * One instance of a sheet in the tree under the top sheet.
 */
export class SheetInstance extends Record {
    constructor(IsInstance, ParamValues, BoundParams) {
        super();
        this.IsInstance = IsInstance;
        this.ParamValues = ParamValues;
        this.BoundParams = BoundParams;
    }
}

export function SheetInstance_$reflection() {
    return record_type("ParameterAnalysis.SheetInstance", [], SheetInstance, () => [["IsInstance", bool_type], ["ParamValues", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [ParamName_$reflection(), option_type(bigint_type)])], ["BoundParams", class_type("Microsoft.FSharp.Collections.FSharpSet`1", [ParamName_$reflection()])]]);
}

/**
 * The parameters a sheet declares, with their defaults and descriptions.
 */
export function declaredParamDefs(ldc) {
    let option_1;
    return defaultArg((option_1 = ldc.LCParameterSlots, (option_1 != null) ? option_1.DefaultBindings : undefined), empty({
        Compare: (x, y) => (compare(x, y) | 0),
    }));
}

/**
 * The parameters a sheet declares as an evaluation environment: its default bindings.
 */
export function declaredParams(ldc) {
    return bindingsOf(declaredParamDefs(ldc));
}

/**
 * The parameterised slots of a sheet.
 */
export function sheetParamSlots(ldc) {
    let option_1;
    return defaultArg((option_1 = ldc.LCParameterSlots, (option_1 != null) ? option_1.ParamSlots : undefined), empty({
        Compare: (x, y) => (compare(x, y) | 0),
    }));
}

/**
 * The custom component instances a sheet contains.
 */
export function customInstances(ldc) {
    return choose((comp) => {
        const matchValue = comp.Type;
        if (matchValue.tag === 26) {
            return [comp, matchValue.fields[0]];
        }
        else {
            return undefined;
        }
    }, ldc.CanvasState[0]);
}

/**
 * The sheets of a project that are not instantiated inside any other sheet: the roots of the
 * instance forest, each the top of its own design. Ordered as the loaded components are.
 */
export function instanceForestRoots(ldcs) {
    const instantiated = ofList(collect((arg) => map((tupledArg) => tupledArg[1].Name, customInstances(arg)), ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    return filter((name) => !contains(name, instantiated), map((ldc_1) => ldc_1.Name, ldcs));
}

/**
 * The sheet the user has flagged as the top, if any. Only for showing the flag; to find the
 * design a sheet belongs to use effectiveTopSheetFor, which is total.
 */
export function flaggedTopSheet(ldcs) {
    const option_1 = tryFind((ldc) => ldc.IsTopSheet, ldcs);
    if (option_1 != null) {
        return option_1.Name;
    }
    else {
        return undefined;
    }
}

function knownBindings(values) {
    return fold((acc, name, value) => {
        if (value == null) {
            return acc;
        }
        else {
            return add(name, new ParamExpression(/* PInt */ 0, [value]), acc);
        }
    }, empty({
        Compare: (x, y) => (compare(x, y) | 0),
    }), values);
}

/**
 * The value of every parameter a sheet declares when its defaults are evaluated in their own
 * environment - the values the sheet displays and checks with when nothing binds it.
 */
export function evaluatedDefaults(defaults) {
    return map_1((_arg, expr) => {
        const matchValue = evaluateParamExpression(defaults, expr);
        if (matchValue.tag === 1) {
            return undefined;
        }
        else {
            return matchValue.fields[0];
        }
    }, defaults);
}

/**
 * The expressions one custom component instance binds its sheet's parameters to, in the
 * parameters of the sheet the instance sits on. A CustomCompParam slot of the parent sheet
 * overrides the binding stored on the instance, exactly as in simulation elaboration.
 */
export function instanceBindingExprs(parentSlots, comp, cc) {
    return fold((acc, slot, exprSpec) => {
        const matchValue = slot.CompSlot;
        let matchResult, p_1;
        if (matchValue.tag === 2) {
            if (equals(slot.CompId, comp.Id)) {
                matchResult = 0;
                p_1 = matchValue.fields[0];
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0:
                return add(new ParamName(p_1), exprSpec.Expression, acc);
            default:
                return acc;
        }
    }, defaultArg(cc.ParameterBindings, empty({
        Compare: (x, y) => (compare(x, y) | 0),
    })), parentSlots);
}

function childParamValues(parentValues, bindingExprs, childDefaults) {
    const parentBindings = knownBindings(parentValues);
    const merged = map_1((name, defExpr) => {
        const matchValue = tryFind_1(name, bindingExprs);
        if (matchValue == null) {
            return defExpr;
        }
        else {
            const matchValue_1 = evaluateParamExpression(parentBindings, matchValue);
            if (matchValue_1.tag === 1) {
                return undefined;
            }
            else {
                return new ParamExpression(/* PInt */ 0, [matchValue_1.fields[0]]);
            }
        }
    }, childDefaults);
    const env = fold((acc, name_1, exprOpt) => {
        if (exprOpt == null) {
            return acc;
        }
        else {
            return add(name_1, exprOpt, acc);
        }
    }, empty({
        Compare: (x, y) => (compare(x, y) | 0),
    }), merged);
    return map_1((name_2, _arg) => {
        const matchValue_2 = flatten(tryFind_1(name_2, merged));
        if (matchValue_2 != null) {
            const matchValue_3 = evaluateParamExpression(env, matchValue_2);
            if (matchValue_3.tag === 1) {
                return undefined;
            }
            else {
                return matchValue_3.fields[0];
            }
        }
        else {
            return undefined;
        }
    }, childDefaults);
}

/**
 * Walk the instance tree under `topSheet` and collect the instances of every sheet with the
 * parameter values they resolve to. This is the binding walk simulation elaboration performs,
 * without building graphs.
 * The walk is memoised on (sheet name, parameter values): the subtree below an instance depends
 * only on those, so a sheet tree recurring with the same values is descended once. The VALUE
 * SETS this produces are complete - a skipped subtree resolves identically to the one already
 * walked. Nothing here answers "by which route": that question is asked only by the bind-to-top
 * chain computation, which works on the sheet DAG so that no path can be missed.
 */
export function analyseUnderTop(ldcs, topSheet) {
    const byName = ofList_1(map((ldc) => [ldc.Name, ldc], ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    const walk = (tupledArg) => ((isInstance) => {
        const acc_1 = tupledArg[0];
        const visited = tupledArg[1];
        return (sheetsOnPath) => ((sheetName_1) => ((values) => ((boundParams) => {
            const matchValue = tryFind_1(sheetName_1, byName);
            if (matchValue != null) {
                const ldc_1 = matchValue;
                let acc_2;
                const acc = acc_1;
                const sheetName = sheetName_1;
                acc_2 = add(sheetName, cons(new SheetInstance(isInstance, values, boundParams), defaultArg(tryFind_1(sheetName, acc), empty_1())), acc);
                const key = [sheetName_1, toList(values)];
                return contains(key, visited) ? [acc_2, visited] : fold_1((accVisited, tupledArg_1) => {
                    const cc = tupledArg_1[1];
                    const matchValue_2 = contains(cc.Name, sheetsOnPath);
                    const matchValue_3 = tryFind_1(cc.Name, byName);
                    let matchResult;
                    if (matchValue_2) {
                        matchResult = 0;
                    }
                    else if (matchValue_3 != null) {
                        matchResult = 1;
                    }
                    else {
                        matchResult = 0;
                    }
                    switch (matchResult) {
                        case 0:
                            return accVisited;
                        default: {
                            const childLdc = matchValue_3;
                            const bindingExprs = instanceBindingExprs(sheetParamSlots(ldc_1), tupledArg_1[0], cc);
                            const childDefaults = declaredParams(childLdc);
                            const childValues = childParamValues(values, bindingExprs, childDefaults);
                            const childBound = ofList(filter((name) => containsKey(name, childDefaults), map((tuple) => tuple[0], toList(bindingExprs))), {
                                Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
                            });
                            return walk(accVisited)(true)(add_1(cc.Name, sheetsOnPath))(cc.Name)(childValues)(childBound);
                        }
                    }
                }, [acc_2, add_1(key, visited)], customInstances(ldc_1));
            }
            else {
                return [acc_1, visited];
            }
        })));
    });
    const matchValue_5 = tryFind_1(topSheet, byName);
    if (matchValue_5 != null) {
        const topValues = evaluatedDefaults(declaredParams(matchValue_5));
        return map_1((_arg, instances) => reverse(instances), walk([empty({
            Compare: (x_3, y_3) => (comparePrimitives(x_3, y_3) | 0),
        }), empty_2({
            Compare: (x_4, y_4) => (compareArrays(x_4, y_4) | 0),
        })])(false)(singleton(topSheet, {
            Compare: (x_5, y_5) => (comparePrimitives(x_5, y_5) | 0),
        }))(topSheet)(topValues)(empty_2({
            Compare: (x_6, y_6) => (compare(x_6, y_6) | 0),
        }))[0]);
    }
    else {
        return empty({
            Compare: (x_2, y_2) => (comparePrimitives(x_2, y_2) | 0),
        });
    }
}

/**
 * What the editor should display for one parameter of a sheet, computed from the value the
 * parameter takes in every instance of the sheet under the top.
 */
export class ParamDisplayValue extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["NotUsed", "Values"];
    }
}

export function ParamDisplayValue_$reflection() {
    return union_type("ParameterAnalysis.ParamDisplayValue", [], ParamDisplayValue, () => [[["Item", bigint_type]], [["Item", list_type(bigint_type)]]]);
}

/**
 * The value the sheet is drawn at, whichever case produced it.
 */
export function shownValue(display) {
    if (display.tag === 1) {
        return head(display.fields[0]);
    }
    else {
        return display.fields[0];
    }
}

function instancesOnly(instances) {
    return filter((inst) => inst.IsInstance, instances);
}

/**
 * The display value of each parameter a sheet declares, given the sheet's records under the
 * top. Unknown (unevaluable) instance values are ignored: checking here must not accuse a
 * design that may be right.
 */
export function displayValuesOfSheet(ldc, instances) {
    const defaults = declaredParams(ldc);
    const defaultValues = evaluatedDefaults(defaults);
    const instances_1 = instancesOnly(instances);
    return map_1((name, _arg) => {
        const storedValue = defaultArg(flatten(tryFind_1(name, defaultValues)), fromOne());
        const values = sortDescending(List_distinct(choose((inst_1) => flatten(tryFind_1(name, inst_1.ParamValues)), filter((inst) => contains(name, inst.BoundParams), instances_1)), {
            Equals: equals_1,
            GetHashCode: (x) => (bigintHash(x) | 0),
        }), {
            Compare: (x_1, y_1) => (compare_1(x_1, y_1) | 0),
        });
        if (isEmpty(values)) {
            return new ParamDisplayValue(/* NotUsed */ 0, [storedValue]);
        }
        else {
            return new ParamDisplayValue(/* Values */ 1, [values]);
        }
    }, defaults);
}

/**
 * The display values of every parameter of `sheetName` under `topSheet`.
 */
export function displayValues(ldcs, topSheet, sheetName) {
    const matchValue = tryFind((ldc) => (ldc.Name === sheetName), ldcs);
    if (matchValue != null) {
        return displayValuesOfSheet(matchValue, defaultArg(tryFind_1(sheetName, analyseUnderTop(ldcs, topSheet)), empty_1()));
    }
    else {
        return empty({
            Compare: (x, y) => (compare(x, y) | 0),
        });
    }
}

/**
 * The sheets in the instance tree under a top sheet (the top included).
 */
export function sheetsUnderTop(ldcs, topSheet) {
    return ofSeq(keys(analyseUnderTop(ldcs, topSheet)), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
}

/**
 * The roots of the instance forest whose design contains the given sheet.
 */
export function rootsContaining(ldcs, sheetName) {
    return filter((root) => contains(sheetName, sheetsUnderTop(ldcs, root)), instanceForestRoots(ldcs));
}

/**
 * The design a sheet belongs to: the sheet whose parameter values everything below it is derived
 * from. TOTAL - there is always an answer, which is what lets every other parameter value be
 * derived rather than guessed.
 * 
 * This used to ask whether the PROJECT had a single design, by looking for exactly one
 * instance-forest root. One stray sheet that nothing instantiates - a scratch sheet, a half-built
 * block - is a second root, so that question answered "no" for the whole project, and every
 * parameter row in every sheet silently fell back to its stored value. The question that matters
 * is which design the sheet being looked at is part of, and a stray sheet elsewhere does not
 * change that.
 * 
 * Several roots reaching one sheet is the genuinely ambiguous case - two designs using the same
 * subsheet at different sizes, which is not the same thing as one design reaching it by two paths
 * - and is what the top-sheet popup asks about. Until it is answered the first root is taken, so
 * that a value is always defined.
 */
export function effectiveTopSheetFor(ldcs, sheetName) {
    const matchValue = flaggedTopSheet(ldcs);
    if (matchValue == null) {
        const matchValue_1 = rootsContaining(ldcs, sheetName);
        if (isEmpty(matchValue_1)) {
            return sheetName;
        }
        else if (isEmpty(tail(matchValue_1))) {
            return head(matchValue_1);
        }
        else {
            return head(matchValue_1);
        }
    }
    else {
        return matchValue;
    }
}

/**
 * Every parameter environment a sheet is used in: one set of values per distinct way its design
 * binds it, and its own declared values where nothing uses it at all.
 * 
 * This is what makes "the width of that memory" a question with several answers. A sheet used
 * twice at different sizes has two environments, so a component on it has two sets of widths, and
 * anything that has to hold for the component - contents fitting a memory, above all - has to hold
 * in every one of them. displayValuesOfSheet answers the same question one parameter at a time,
 * which cannot be used here: two parameters' values must be taken from the SAME instance, and the
 * per-parameter answer has already forgotten which instance each value came from.
 * 
 * Unknown values are dropped rather than guessed, so an expression using one fails to evaluate and
 * the caller is left without an answer instead of with a wrong one.
 */
export function bindingEnvironmentsOf(ldcs, sheetName) {
    const matchValue = tryFind((ldc) => (ldc.Name === sheetName), ldcs);
    if (matchValue != null) {
        const ldc_1 = matchValue;
        const instances_1 = instancesOnly(defaultArg(tryFind_1(sheetName, analyseUnderTop(ldcs, effectiveTopSheetFor(ldcs, sheetName))), empty_1()));
        if (isEmpty(instances_1)) {
            return singleton_1(declaredParams(ldc_1));
        }
        else {
            return List_distinct(map((inst) => knownBindings(inst.ParamValues), instances_1), {
                Equals: (x_1, y_1) => x_1.Equals(y_1),
                GetHashCode: (x_1) => (safeHash(x_1) | 0),
            });
        }
    }
    else {
        return singleton_1(empty({
            Compare: (x, y) => (compare(x, y) | 0),
        }));
    }
}

/**
 * The address and word widths one memory component has across the whole design: one pair per
 * environment the sheet it sits on is used in, without duplicates.
 * 
 * A width that is not parameterised, or whose expression will not evaluate in some environment, is
 * the one the component is carrying - which is what the sheet is drawn at, and the only answer
 * there is. So this is never empty, and never invents a pairing: both widths of a pair come from
 * the same environment, and crossing the two lists would make sizes no instance has.
 */
export function memoryWidthsInDesign(ldcs, sheetName, compId, mem) {
    let option_1;
    const slots = defaultArg((option_1 = tryFind((ldc) => (ldc.Name === sheetName), ldcs), (option_1 != null) ? sheetParamSlots(option_1) : undefined), empty({
        Compare: (x, y) => (compare(x, y) | 0),
    }));
    const widthOf = (slotName, stored, bindings) => {
        let option_4, matchValue_1;
        return defaultArg((option_4 = tryPick((slot, exprSpec) => {
            if (sameSlot(slot, new ParamSlot(compId, slotName))) {
                return exprSpec.Expression;
            }
            else {
                return undefined;
            }
        }, slots), (option_4 != null) ? ((matchValue_1 = evaluateParamExpression(bindings, option_4), (matchValue_1.tag === 1) ? undefined : tryIntOfParamInt(matchValue_1.fields[0]))) : undefined), stored) | 0;
    };
    return List_distinct(map((bindings_1) => [widthOf(CompSlotName.MemoryAddressWidth, mem.AddressWidth, bindings_1), widthOf(CompSlotName.MemoryWordWidth, mem.WordWidth, bindings_1)], bindingEnvironmentsOf(ldcs, sheetName)), {
        Equals: equalArrays,
        GetHashCode: (x_1) => (arrayHash(x_1) | 0),
    });
}

/**
 * Every sheet brought into line with what its design sets its parameters to: the parameter values
 * a design settles are written into the sheet, and its parameterised slots are rewritten at them.
 * 
 * A pure recomputation from the primary state - each design's top-sheet values, and the bindings
 * on instances - rather than an incremental edit. That is what makes it safe to run after
 * anything: it is idempotent and order-independent, so undo need only restore the primary state
 * and run this again, and no change has to reason about which sheets a binding might reach.
 * 
 * A parameter nothing sets is left exactly as it is. Its stored value IS the primary state for
 * that sheet, and overwriting it would destroy the only copy.
 */
export function propagateParameterValues(ldcs) {
    let analysed;
    const matchValue = flaggedTopSheet(ldcs);
    if (matchValue == null) {
        analysed = map((root) => [root, analyseUnderTop(ldcs, root)], instanceForestRoots(ldcs));
    }
    else {
        const flagged = matchValue;
        analysed = singleton_1([flagged, analyseUnderTop(ldcs, flagged)]);
    }
    return map((ldc) => {
        let sheetName, matchValue_1, tupledArg_3;
        const matchValue_2 = ldc.LCParameterSlots;
        if (matchValue_2 != null) {
            const defs = matchValue_2;
            const newBindings = fold_1((bindings, tupledArg_2) => {
                const name_1 = tupledArg_2[0];
                const matchValue_3 = tryFind_1(name_1, bindings);
                if (matchValue_3 == null) {
                    return bindings;
                }
                else {
                    return add(name_1, new ParamDefinition(new ParamExpression(/* PInt */ 0, [tupledArg_2[1]]), matchValue_3.Description), bindings);
                }
            }, defs.DefaultBindings, choose((tupledArg_1) => {
                const d = tupledArg_1[1];
                let matchResult, largest;
                if (d.tag === 0) {
                    matchResult = 1;
                }
                else if (isEmpty(d.fields[0])) {
                    matchResult = 1;
                }
                else {
                    matchResult = 0;
                    largest = head(d.fields[0]);
                }
                switch (matchResult) {
                    case 0:
                        return [tupledArg_1[0], largest];
                    default:
                        return undefined;
                }
            }, toList(displayValuesOfSheet(ldc, (sheetName = ldc.Name, (matchValue_1 = filter((tupledArg) => containsKey(sheetName, tupledArg[1]), analysed), isEmpty(matchValue_1) ? defaultArg(tryFind_1(sheetName, analyseUnderTop(ldcs, sheetName)), empty_1()) : defaultArg(tryFind_1(sheetName, head(matchValue_1)[1]), empty_1())))))));
            const newDefs = new ParameterDefs(newBindings, defs.ParamSlots);
            return new LoadedComponent(ldc.Name, ldc.TimeStamp, ldc.FilePath, ldc.WaveInfo, (tupledArg_3 = ldc.CanvasState, resolveCanvasAtBindings(bindingsOf(newBindings), newDefs.ParamSlots, tupledArg_3[0], tupledArg_3[1])), ldc.InputLabels, ldc.OutputLabels, newDefs, ldc.Form, ldc.LoadedComponentIsOutOfDate, ldc.Description, ldc.IsTopSheet, ldc.ArrayInfo);
        }
        else {
            return ldc;
        }
    }, ldcs);
}

function isFromLibrary(ldc) {
    const matchValue = ldc.Form;
    let matchResult;
    if (matchValue != null) {
        if (matchValue.tag === 1) {
            matchResult = 0;
        }
        else {
            matchResult = 1;
        }
    }
    else {
        matchResult = 1;
    }
    switch (matchResult) {
        case 0:
            return true;
        default:
            return false;
    }
}

/**
 * Gate A: does this project use parameters at all?
 * 
 * Library sheets do not count. Their parameters arrived with the library rather than being
 * declared by the user, and on a library component instance they are presented as ordinary
 * settings - so placing one must not turn the parameter vocabulary on across the whole project.
 */
export function projectDeclaresParams(ldcs) {
    return exists((ldc_1) => !isEmpty_1(declaredParamDefs(ldc_1)), filter((arg) => !isFromLibrary(arg), ldcs));
}

/**
 * Give every instance of `sheetName` a binding for a parameter just declared on it.
 * 
 * Every instance binds every parameter its sheet declares. Placing one establishes that, but a
 * parameter added to a sheet that ALREADY has instances would leave all of them binding nothing,
 * and an unbound parameter is a state the design deliberately does not have: it elaborates at the
 * sheet's own declared value, which is a fact about the sheet rather than about the instance, and
 * it makes "default" into a concept the user has to reason about.
 * 
 * The value bound is the one just declared, so the design is unchanged - that is exactly what an
 * unbound parameter elaborated to. What changes is that the binding exists, can be seen and
 * edited, and, being a literal, is what findBindOffers fires on.
 * 
 * No slot is created: a literal needs none, as updateParamSlot has it. A sheet cannot instantiate
 * itself, so the sheet gaining the parameter is skipped.
 */
export function bindParamOnInstances(sheetName, name, value, ldcs) {
    return map((ldc_1) => {
        if (ldc_1.Name === sheetName) {
            return ldc_1;
        }
        else {
            const ldc = ldc_1;
            const patternInput = ldc.CanvasState;
            const ldc$0027 = new LoadedComponent(ldc.Name, ldc.TimeStamp, ldc.FilePath, ldc.WaveInfo, [map((comp) => {
                const matchValue = comp.Type;
                let matchResult, custom_1;
                if (matchValue.tag === 26) {
                    if (matchValue.fields[0].Name === sheetName) {
                        matchResult = 0;
                        custom_1 = matchValue.fields[0];
                    }
                    else {
                        matchResult = 1;
                    }
                }
                else {
                    matchResult = 1;
                }
                switch (matchResult) {
                    case 0: {
                        const bindings = defaultArg(custom_1.ParameterBindings, empty({
                            Compare: (x, y) => (compare(x, y) | 0),
                        }));
                        if (containsKey(name, bindings)) {
                            return comp;
                        }
                        else {
                            return new Component(comp.Id, new ComponentType(/* Custom */ 26, [new CustomComponentType(custom_1.Name, custom_1.InputLabels, custom_1.OutputLabels, custom_1.Form, add(name, new ParamExpression(/* PInt */ 0, [value]), bindings), custom_1.Description)]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
                        }
                    }
                    default:
                        return comp;
                }
            }, patternInput[0]), patternInput[1]], ldc.InputLabels, ldc.OutputLabels, ldc.LCParameterSlots, ldc.Form, ldc.LoadedComponentIsOutOfDate, ldc.Description, ldc.IsTopSheet, ldc.ArrayInfo);
            if (equalArrays(ldc$0027.CanvasState, ldc.CanvasState)) {
                return ldc;
            }
            else {
                return new LoadedComponent(ldc$0027.Name, ldc$0027.TimeStamp, ldc$0027.FilePath, ldc$0027.WaveInfo, ldc$0027.CanvasState, ldc$0027.InputLabels, ldc$0027.OutputLabels, ldc$0027.LCParameterSlots, ldc$0027.Form, true, ldc$0027.Description, ldc$0027.IsTopSheet, ldc$0027.ArrayInfo);
            }
        }
    }, ldcs);
}

/**
 * Mark a sheet as differing from the file it was loaded from.
 * 
 * A change to what a sheet DECLARES, or to the expression filling one of its slots, need not
 * change its canvas at all: declaring a parameter, writing its description, deleting an unused
 * one, or entering an expression that works out to the width already shown all leave the canvas
 * identical. Issie decides whether the open sheet needs saving by comparing canvases
 * (UpdateHelpers.currentSheetIsOutOfDate), so a change of that kind is invisible to it: the save
 * button stays dark, switching sheets does not save, and the work is dropped.
 * 
 * This flag is the one other thing that comparison consults, and saving the sheet clears it. It
 * is what ParameterView.markSheetParamsChanged sets on every path that edits parameter data.
 */
export function markSheetOutOfDate(sheetName, ldcs) {
    return map((ldc) => {
        if (ldc.Name === sheetName) {
            return new LoadedComponent(ldc.Name, ldc.TimeStamp, ldc.FilePath, ldc.WaveInfo, ldc.CanvasState, ldc.InputLabels, ldc.OutputLabels, ldc.LCParameterSlots, ldc.Form, true, ldc.Description, ldc.IsTopSheet, ldc.ArrayInfo);
        }
        else {
            return ldc;
        }
    }, ldcs);
}

/**
 * Whether every instance of every sheet binds every parameter that sheet declares.
 * The invariant bindParamOnInstances and the placement popup exist to keep; false only for a
 * project saved before it was required, or one edited by hand.
 * Give every instance a binding for every parameter its sheet declares, at that sheet's own
 * value, wherever one is missing.
 * 
 * Every instance binding every parameter is an invariant the rest of the parameter system is
 * written against: placing an instance asks for each value, and adding a parameter to a sheet
 * binds it on the instances that already exist. Only a project saved before that was required, or
 * one edited by hand, can arrive without it - so it is repaired on load rather than guarded
 * against everywhere. An invariant that almost always holds buys nothing: every reader still has
 * to handle the case it does not.
 * 
 * The value used is the child sheet's own, which is what such an instance was already resolving
 * to, so this changes no design - it only writes down what was already true.
 */
export function bindMissingInstanceParams(ldcs) {
    const byName = ofList_1(map((ldc) => [ldc.Name, ldc], ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    return map((parentLdc) => {
        const patternInput = parentLdc.CanvasState;
        const comps = patternInput[0];
        const matchValue_3 = map((comp) => {
            const matchValue = comp.Type;
            if (matchValue.tag === 26) {
                const cc = matchValue.fields[0];
                const matchValue_1 = tryFind_1(cc.Name, byName);
                if (matchValue_1 != null) {
                    const childLdc = matchValue_1;
                    const alreadyBound = instanceBindingExprs(sheetParamSlots(parentLdc), comp, cc);
                    const stored = defaultArg(cc.ParameterBindings, empty({
                        Compare: (x_1, y_1) => (compare(x_1, y_1) | 0),
                    }));
                    const missing = filter_1((name, _arg) => !containsKey(name, alreadyBound), declaredParams(childLdc));
                    if (isEmpty_1(missing)) {
                        return comp;
                    }
                    else {
                        const defaults = evaluatedDefaults(declaredParams(childLdc));
                        return new Component(comp.Id, new ComponentType(/* Custom */ 26, [new CustomComponentType(cc.Name, cc.InputLabels, cc.OutputLabels, cc.Form, fold((acc, name_1, _arg_1) => add(name_1, new ParamExpression(/* PInt */ 0, [defaultArg(flatten(tryFind_1(name_1, defaults)), fromOne())]), acc), stored, missing), cc.Description)]), comp.Label, comp.InputPorts, comp.OutputPorts, comp.X, comp.Y, comp.H, comp.W, comp.SymbolInfo, comp.SlotInfo);
                    }
                }
                else {
                    return comp;
                }
            }
            else {
                return comp;
            }
        }, comps);
        if (equals(matchValue_3, comps)) {
            return parentLdc;
        }
        else {
            return new LoadedComponent(parentLdc.Name, parentLdc.TimeStamp, parentLdc.FilePath, parentLdc.WaveInfo, [matchValue_3, patternInput[1]], parentLdc.InputLabels, parentLdc.OutputLabels, parentLdc.LCParameterSlots, parentLdc.Form, true, parentLdc.Description, parentLdc.IsTopSheet, parentLdc.ArrayInfo);
        }
    }, ldcs);
}

export function everyInstanceBindsEveryParam(ldcs) {
    const byName = ofList_1(map((ldc) => [ldc.Name, ldc], ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    return forAll((parentLdc) => forAll((tupledArg) => {
        const cc = tupledArg[1];
        const matchValue = tryFind_1(cc.Name, byName);
        if (matchValue != null) {
            const childLdc = matchValue;
            const bound = instanceBindingExprs(sheetParamSlots(parentLdc), tupledArg[0], cc);
            return forAll_1((name, _arg) => containsKey(name, bound), declaredParams(childLdc));
        }
        else {
            return true;
        }
    }, customInstances(parentLdc)), ldcs);
}

function displayValuesUnderRoot(ldcs, root) {
    const instancesBySheet = analyseUnderTop(ldcs, root);
    return (ldc) => {
        const option_1 = tryFind_1(ldc.Name, instancesBySheet);
        return (option_1 != null) ? map_1((_arg, display) => shownValue(display), displayValuesOfSheet(ldc, option_1)) : undefined;
    };
}

function displayableSheets(ldcs) {
    return filter((arg) => !isFromLibrary(arg), ldcs);
}

/**
 * Gate B: does this project need a top sheet?
 * 
 * A top sheet exists to settle WHICH VALUES a sheet is drawn at when its instances disagree. The
 * presence of a parameter is not that question: a sheet with one instance, or whose instances all
 * agree, or which is not instantiated at all, has nothing to settle, and none of the top-sheet
 * apparatus should appear for it.
 * 
 * Only sheets the user can open are asked the question. A library sheet is never displayed, so
 * there is no value to choose for it - but a library instance whose parameter is bound to an
 * expression rather than a literal takes its value from the parent sheet, so when the parent
 * varies the PARENT is ambiguous and is caught here on its own account. Nothing needs to
 * special-case libraries beyond not asking about them.
 * 
 * Ambiguity means two DESIGNS disagreeing about a sheet, not one design reaching it by two paths.
 * The second is ordinary - a sheet used at two sizes within one design - and is settled by taking
 * the largest, with the others shown beside it. The first cannot be settled by any rule: the two
 * designs are both right, and only the user can say which one they are looking at.
 * 
 * The values each root gives the sheet are therefore compared root by root. Merging the instances
 * of every root into one bag, as this used to, made the two indistinguishable and raised the
 * top-sheet question for designs that had nothing to decide.
 */
export function projectHasAmbiguousDisplay(ldcs) {
    const byRoot = map((root) => displayValuesUnderRoot(ldcs, root), instanceForestRoots(ldcs));
    return exists((ldc) => (length(List_distinct(choose((valuesUnder) => valuesUnder(ldc), byRoot), {
        Equals: (x, y) => x.Equals(y),
        GetHashCode: (x) => (safeHash(x) | 0),
    })) > 1), displayableSheets(ldcs));
}

/**
 * Whether naming this sheet as the top would settle anything - which is what decides whether the
 * offer to do so is worth making.
 * 
 * Three things have to hold at once, and each of them alone is ordinary:
 * 
 * 1. the sheet is a root. A sheet inside another one is not a design of its own; making it the
 * top would say the design it sits in is not the one being looked at, which is not what
 * anyone means by right-clicking a subsheet.
 * 2. a sheet it reaches is reached by a different root too. One design owning all of its
 * subsheets has no second opinion to choose between.
 * 3. the two roots give that shared sheet different parameter values. This is the whole content
 * of the choice: the top sheet exists to say WHICH VALUES a sheet is drawn at, so where the
 * designs agree there is nothing to say. A shared sheet with no parameters agrees trivially,
 * which is why an ordinary project never sees the offer at all.
 * 
 * Read off the roots rather than off the flag, so the sheet already flagged as top still offers
 * it: the flag is how the choice is moved, and a project with a real choice in it has to keep
 * every way of making it.
 */
export function topSheetChoiceMatters(ldcs, sheetName) {
    const roots = instanceForestRoots(ldcs);
    const matchValue = contains_1(sheetName, roots, {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    });
    if (matchValue) {
        const valuesHere = displayValuesUnderRoot(ldcs, sheetName);
        const valuesElsewhere = map((root) => displayValuesUnderRoot(ldcs, root), filter((y_1) => (sheetName !== y_1), roots));
        return exists((ldc) => {
            const matchValue_1 = valuesHere(ldc);
            if (matchValue_1 != null) {
                const mine = matchValue_1;
                return exists((valuesUnder) => {
                    const matchValue_2 = valuesUnder(ldc);
                    if (matchValue_2 == null) {
                        return false;
                    }
                    else {
                        const theirs = matchValue_2;
                        return !theirs.Equals(mine);
                    }
                }, valuesElsewhere);
            }
            else {
                return false;
            }
        }, displayableSheets(ldcs));
    }
    else {
        return false;
    }
}

/**
 * One modification the bind-to-top offer would make if accepted.
 */
export class ChainAction extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["AddSheetParam", "BindInstance"];
    }
}

export function ChainAction_$reflection() {
    return union_type("ParameterAnalysis.ChainAction", [], ChainAction, () => [[["Sheet", string_type], ["Param", ParamName_$reflection()], ["Default", bigint_type], ["Description", string_type]], [["Sheet", string_type], ["InstanceId", int32_type], ["InstanceLabel", string_type], ["ChildSheet", string_type], ["Param", ParamName_$reflection()]]]);
}

/**
 * An offer to bind one unbound instance parameter to a same-named parameter on an ancestor
 * sheet, by materialising parameters and bindings along every instance path from the ancestor
 * down to the instance.
 */
export class BindOffer extends Record {
    constructor(OnSheet, InstanceId, InstanceLabel, ChildSheet, Param, BindsTo, Declarers, Actions) {
        super();
        this.OnSheet = OnSheet;
        this.InstanceId = InstanceId;
        this.InstanceLabel = InstanceLabel;
        this.ChildSheet = ChildSheet;
        this.Param = Param;
        this.BindsTo = BindsTo;
        this.Declarers = Declarers;
        this.Actions = Actions;
    }
}

export function BindOffer_$reflection() {
    return record_type("ParameterAnalysis.BindOffer", [], BindOffer, () => [["OnSheet", string_type], ["InstanceId", int32_type], ["InstanceLabel", string_type], ["ChildSheet", string_type], ["Param", ParamName_$reflection()], ["BindsTo", string_type], ["Declarers", list_type(string_type)], ["Actions", list_type(ChainAction_$reflection())]]);
}

/**
 * The sheets an offer's actions modify (parameters added or instance bindings created).
 */
export function sheetsModifiedByOffer(offer) {
    return List_distinct(map((_arg) => {
        if (_arg.tag === 1) {
            return _arg.fields[0];
        }
        else {
            return _arg.fields[0];
        }
    }, offer.Actions), {
        Equals: (x, y) => (x === y),
        GetHashCode: (x) => (stringHash(x) | 0),
    });
}

function edgesUnderTop(byName, underTop) {
    return collect((ldc) => map((tupledArg_1) => {
        const comp = tupledArg_1[0];
        return new InstancePathLink(ldc.Name, comp.Id, comp.Label, tupledArg_1[1].Name);
    }, filter((tupledArg) => contains(tupledArg[1].Name, underTop), customInstances(ldc))), choose((name) => tryFind_1(name, byName), toList_1(underTop)));
}

function sheetsReaching(edges, target) {
    const grow = (reaching_mut) => {
        grow:
        while (true) {
            const reaching = reaching_mut;
            const more = ofList(map((e_1) => e_1.ParentSheet, filter((e) => {
                if (contains(e.ChildSheet, reaching)) {
                    return !contains(e.ParentSheet, reaching);
                }
                else {
                    return false;
                }
            }, edges)), {
                Compare: (x, y) => (comparePrimitives(x, y) | 0),
            });
            if (isEmpty_2(more)) {
                return reaching;
            }
            else {
                reaching_mut = union(reaching, more);
                continue grow;
            }
            break;
        }
    };
    return grow(singleton(target, {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    }));
}

function sheetsReachableFrom(edges, source) {
    const grow = (reachable_mut) => {
        grow:
        while (true) {
            const reachable = reachable_mut;
            const more = ofList(map((e_1) => e_1.ChildSheet, filter((e) => {
                if (contains(e.ParentSheet, reachable)) {
                    return !contains(e.ChildSheet, reachable);
                }
                else {
                    return false;
                }
            }, edges)), {
                Compare: (x, y) => (comparePrimitives(x, y) | 0),
            });
            if (isEmpty_2(more)) {
                return reachable;
            }
            else {
                reachable_mut = union(reachable, more);
                continue grow;
            }
            break;
        }
    };
    return grow(singleton(source, {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    }));
}

function chainActionsOnSheet(byName, underTop, edges, name, onSheet, finalLink) {
    let option_6, option_4, option_14, option_12, option_10;
    const declares = (sheetName) => {
        let option_1;
        return defaultArg((option_1 = tryFind_1(sheetName, byName), (option_1 != null) ? containsKey(name, declaredParams(option_1)) : undefined), false);
    };
    const declarers = filter_2(declares, intersect(underTop, sheetsReaching(edges, onSheet)));
    if (isEmpty_2(declarers)) {
        return undefined;
    }
    else {
        const chainSheets = unionMany(map((declarer) => intersect(sheetsReachableFrom(edges, declarer), sheetsReaching(edges, onSheet)), toList_1(declarers)), {
            Compare: (x, y) => (comparePrimitives(x, y) | 0),
        });
        const rootDeclarer = minElement(declarers);
        const rootDefault = defaultArg(flatten((option_6 = ((option_4 = tryFind_1(rootDeclarer, byName), (option_4 != null) ? evaluatedDefaults(declaredParams(option_4)) : undefined)), (option_6 != null) ? tryFind_1(name, option_6) : undefined)), fromOne());
        const rootDescription = defaultArg((option_14 = ((option_12 = ((option_10 = tryFind_1(rootDeclarer, byName), (option_10 != null) ? declaredParamDefs(option_10) : undefined)), (option_12 != null) ? tryFind_1(name, option_12) : undefined)), (option_14 != null) ? option_14.Description : undefined), "");
        const chainEdges = append(filter((e) => {
            if (contains(e.ParentSheet, chainSheets)) {
                return contains(e.ChildSheet, chainSheets);
            }
            else {
                return false;
            }
        }, edges), ofArray(toArray(finalLink)));
        return [declarers, List_distinct(append(map((sheetName_3) => (new ChainAction(/* AddSheetParam */ 0, [sheetName_3, name, rootDefault, rootDescription])), filter((arg_2) => !declares(arg_2), toList_1(chainSheets))), map((e_1) => (new ChainAction(/* BindInstance */ 1, [e_1.ParentSheet, e_1.InstanceId, e_1.InstanceLabel, e_1.ChildSheet, name])), filter((arg_3) => {
            let link, matchValue_1, parentLdc, option_21, option_19, option_17, comp_1, matchValue_2;
            return !((link = arg_3, (matchValue_1 = tryFind_1(link.ParentSheet, byName), (matchValue_1 != null) && ((parentLdc = matchValue_1, defaultArg((option_21 = ((option_19 = ((option_17 = tryFind((comp) => equals(comp.Id, link.InstanceId), parentLdc.CanvasState[0]), (option_17 != null) ? ((comp_1 = option_17, (matchValue_2 = comp_1.Type, (matchValue_2.tag === 26) ? instanceBindingExprs(sheetParamSlots(parentLdc), comp_1, matchValue_2.fields[0]) : undefined))) : undefined)), (option_19 != null) ? tryFind_1(name, option_19) : undefined)), (option_21 != null) ? exprContainsParams(option_21) : undefined), false))))));
        }, chainEdges))), {
            Equals: equals,
            GetHashCode: (x_1) => (safeHash(x_1) | 0),
        })];
    }
}

/**
 * The bind-to-top offers that qualify under `topSheet`, optionally restricted to instances on
 * one sheet (`onSheet`). An offer exists for each unbound parameter of a custom component
 * instance in the tree under the top whose name is declared on an ancestor sheet along the
 * instance path - the evidence gate. Accepting materialises the chain along every instance
 * path from the ancestors to the instance.
 */
export function findBindOffers(ldcs, topSheet, onSheet) {
    const byName = ofList_1(map((ldc) => [ldc.Name, ldc], ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    const underTop = ofSeq(keys(analyseUnderTop(ldcs, topSheet)), {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    });
    const edges = edgesUnderTop(byName, underTop);
    return collect((sheetName_1) => {
        const matchValue_2 = tryFind_1(sheetName_1, byName);
        if (matchValue_2 != null) {
            const parentLdc_1 = matchValue_2;
            return collect((tupledArg) => {
                let cc, matchValue, childLdc, bound;
                const comp_1 = tupledArg[0];
                const cc_1 = tupledArg[1];
                return choose((name_1) => {
                    const option_2 = chainActionsOnSheet(byName, underTop, edges, name_1, sheetName_1, new InstancePathLink(sheetName_1, comp_1.Id, comp_1.Label, cc_1.Name));
                    if (option_2 != null) {
                        const tupledArg_1 = option_2;
                        const declarers = tupledArg_1[0];
                        const actions = tupledArg_1[1];
                        if (isEmpty(actions)) {
                            return undefined;
                        }
                        else {
                            return new BindOffer(sheetName_1, comp_1.Id, comp_1.Label, cc_1.Name, name_1, contains(topSheet, declarers) ? topSheet : minElement(declarers), toList_1(declarers), actions);
                        }
                    }
                    else {
                        return undefined;
                    }
                }, (cc = cc_1, (matchValue = tryFind_1(cc.Name, byName), (matchValue != null) ? ((childLdc = matchValue, (bound = instanceBindingExprs(sheetParamSlots(parentLdc_1), comp_1, cc), filter((name) => {
                    const matchValue_1 = tryFind_1(name, bound);
                    if (matchValue_1 != null) {
                        return !exprContainsParams(matchValue_1);
                    }
                    else {
                        return true;
                    }
                }, map((tuple) => tuple[0], toList(declaredParams(childLdc))))))) : empty_1())));
            }, customInstances(parentLdc_1));
        }
        else {
            return empty_1();
        }
    }, filter((sheetName) => forAll_2((s) => (s === sheetName), toArray(onSheet)), toList_1(underTop)));
}

/**
 * The same offer, for an instance that has not been placed yet.
 * 
 * Placing an instance and editing a placed one ask the identical question - can this parameter
 * follow a same-named parameter of an ancestor, and what would that take - so they must give the
 * identical answer. The popup used to test only whether the IMMEDIATE parent declared the name,
 * which offered the chain in the one case the user did not need it (they could type the name)
 * and withheld it in the case they did: parameter scoping is single level, so a name declared
 * two sheets up cannot be reached by typing anything.
 * 
 * Returns the ancestor the offer describes itself as following, and everything accepting would
 * do APART from binding the new instance, which the caller does directly.
 */
export function bindOfferForPlacement(ldcs, topSheet, onSheet, name) {
    let tupledArg, declarers;
    const byName = ofList_1(map((ldc) => [ldc.Name, ldc], ldcs), {
        Compare: (x, y) => (comparePrimitives(x, y) | 0),
    });
    const underTop = ofSeq(keys(analyseUnderTop(ldcs, topSheet)), {
        Compare: (x_1, y_1) => (comparePrimitives(x_1, y_1) | 0),
    });
    const option_1 = chainActionsOnSheet(byName, underTop, edgesUnderTop(byName, underTop), name, onSheet, undefined);
    if (option_1 != null) {
        return (tupledArg = option_1, (declarers = tupledArg[0], [contains(topSheet, declarers) ? topSheet : minElement(declarers), tupledArg[1]]));
    }
    else {
        return undefined;
    }
}

